import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  Box,
  Typography,
  Alert,
  Skeleton,
  Card,
  CardContent,
} from '@mui/material'
import { type GridColDef, type GridRowParams, type GridRenderCellParams } from '@mui/x-data-grid'
import { useNavigate } from "react-router-dom"

import { handleApiError, usersService, type User } from "../api"
import { AdminDataGrid } from "../components/admin-data-grid"
import { LinkCell, DateCell, StatusChip } from "../components/data-grid-cells"
import { getRoleColor } from "../components/data-grid-utils"
import { AuthCodeDialog } from "../components/users/AuthCodeDialog"
import { ChangeRoleDialog } from "../components/users/ChangeRoleDialog"
import { ResetPasswordDialog } from "../components/users/ResetPasswordDialog"
import { useUser } from "../hooks/use-user"
import { isAdminUser } from "../lib/user-utils"

import type { UserRole } from "../constants/roles"

type StaffRoleUsersPageProps = {
  role: UserRole
  title: string
}

export default function StaffRoleUsersPage({ role, title }: StaffRoleUsersPageProps) {
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const fetchIdRef = useRef(0)
  const { user: currentUser } = useUser()
  const navigate = useNavigate()

  const isAdmin = isAdminUser(currentUser)

  const fetchUsers = useCallback(async () => {
    const id = ++fetchIdRef.current
    try {
      setIsLoading(true)
      setError("")
      const data = await usersService.getAllUsers(role)
      if (fetchIdRef.current !== id) return
      setUsers(data)
    } catch (err) {
      if (fetchIdRef.current !== id) return
      setError(handleApiError(err))
    } finally {
      if (fetchIdRef.current === id) setIsLoading(false)
    }
  }, [role])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <LinkCell to={`/users/${params.row.id}`}>
          {params.value}
        </LinkCell>
      ),
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      flex: 0.8,
      minWidth: 160,
      renderCell: (params) => (
        <LinkCell to={`/users/${params.row.id}`}>
          {params.value || '-'}
        </LinkCell>
      ),
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      flex: 0.8,
      minWidth: 160,
      renderCell: (params) => (
        <LinkCell to={`/users/${params.row.id}`}>
          {params.value || '-'}
        </LinkCell>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 120,
      renderCell: (params) => (
        <StatusChip
          label={(params.value as string).toUpperCase()}
          color={getRoleColor(params.value as string)}
        />
      ),
    },
    {
      field: 'isVerified',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <StatusChip
          label={params.value ? 'Verified' : 'Unverified'}
          color={params.value ? 'success' : 'warning'}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      flex: 0.8,
      minWidth: 160,
      renderCell: (params) => (
        <DateCell value={params.value as string} stacked />
      ),
    },
    ...(isAdmin ? [{
      field: 'actions',
      headerName: 'Actions',
      width: 300,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1, py: 0.5 }}>
          <ChangeRoleDialog user={params.row as User} onRoleChanged={fetchUsers} />
          <AuthCodeDialog user={params.row as User} />
          <ResetPasswordDialog user={params.row as User} />
        </Box>
      ),
    }] : []),
  ], [isAdmin, fetchUsers])

  if (isLoading) {
    return (
      <Box sx={{ flex: 1, p: 3 }}>
        <Card sx={{ height: 'fit-content' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Skeleton variant="text" width={300} height={40} />
            </Box>
            <Box sx={{ height: 400 }}>
              <Skeleton variant="rectangular" width="100%" height="100%" />
            </Box>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      <Card sx={{ height: 'fit-content' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" fontWeight="bold">
              {title} ({users.length} total)
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <AdminDataGrid
                rows={users}
                columns={columns}
                rowHeight={80}
                onRowClick={(params: GridRowParams) => navigate(`/users/${params.id}`)}
              />
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </Box>
  )
}
